import { DiscordSDK } from "@discord/embedded-app-sdk";

import type { DiscordBridge, DiscordSession } from "./DiscordBridge.ts";

const CDN = "https://cdn.discordapp.com";

function avatarUrlFor(user: { id: string; avatar?: string | null | undefined }): string {
  if (user.avatar) return `${CDN}/avatars/${user.id}/${user.avatar}.png?size=128`;
  const index = Number((BigInt(user.id) >> 22n) % 6n);
  return `${CDN}/embed/avatars/${index}.png`;
}

async function exchangeCode(code: string): Promise<string> {
  // The real bridge only ever runs inside Discord's iframe, where requests
  // after the initial page load must use the /.proxy prefix.
  const response = await fetch("/.proxy/api/token", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!response.ok) throw new Error(`token exchange failed with ${response.status}`);
  const payload: unknown = await response.json();
  const token =
    typeof payload === "object" && payload !== null
      ? (payload as Record<string, unknown>).access_token
      : undefined;
  if (typeof token !== "string") throw new Error("token exchange returned no access_token");
  return token;
}

/**
 * The real thing: runs only inside Discord's iframe. Order matters and is
 * fixed by the SDK: ready, authorize (may open a consent modal once),
 * exchange the code server-side, authenticate with the resulting token.
 */
export class RealDiscordBridge implements DiscordBridge {
  private readonly _sdk: DiscordSDK;

  /**
   * The SDK allows authenticate only once per iframe, so a retry after a
   * failure further down the chain must reuse the session, not redo OAuth.
   */
  private _session: DiscordSession | null = null;

  constructor(clientId: string) {
    if (!clientId) throw new Error("VITE_DISCORD_CLIENT_ID is not set");
    this._sdk = new DiscordSDK(clientId);
  }

  async connect(onStatus?: (status: string) => void): Promise<DiscordSession> {
    if (this._session) return this._session;
    const step = (status: string) => {
      console.info(`[bridge] ${status}`);
      onStatus?.(status);
    };

    step("Waiting for Discord");
    await this._sdk.ready();

    step("Requesting authorization");
    const { code } = await this._sdk.commands.authorize({
      client_id: this._sdk.clientId,
      response_type: "code",
      state: "",
      prompt: "none",
      scope: ["identify"],
    });

    step("Signing in");
    const accessToken = await exchangeCode(code);

    step("Verifying session");
    const auth = await this._sdk.commands.authenticate({ access_token: accessToken });

    const channelId = this._sdk.channelId;
    if (!channelId) throw new Error("Odds Are must be launched from a voice channel");

    this._session = {
      userId: auth.user.id,
      displayName: auth.user.global_name ?? auth.user.username,
      avatarUrl: avatarUrlFor(auth.user),
      channelId,
      accessToken,
    };
    return this._session;
  }
}
